import React, { useState, useEffect } from 'react';
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import { AppBar, Toolbar, Typography, IconButton, makeStyles, Tooltip, Avatar, Badge } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import ChatIcon from '@material-ui/icons/Chat';
import FeedbackIcon from '@material-ui/icons/Feedback';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import Sidebar from '../components/Sidebar';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    background: theme.palette.background.default,
    minHeight: '100vh' },
  appBar: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    background: theme.palette.type === 'dark'
      ? 'rgba(15,23,42,0.85) !important'
      : 'rgba(245,240,235,0.85) !important',
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.08) !important'
      : '1px solid rgba(45,42,36,0.06) !important',
    boxShadow: 'none !important' },
  toolbar: {
    ...theme.mixins.toolbar,
    minHeight: '64px !important' },
  toolbarInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%' },
  titleArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 12 },
  titleDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    boxShadow: '0 0 10px rgba(16,185,129,0.6)' },
  title: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    letterSpacing: '-0.01em' },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: theme.palette.type === 'dark'
      ? 'rgba(148,163,184,0.06)'
      : 'rgba(45,42,36,0.04)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.12)'
      : '1px solid rgba(45,42,36,0.08)',
    color: theme.palette.text.secondary,
    '&:hover': {
      background: 'rgba(16,185,129,0.1)',
      borderColor: 'rgba(16,185,129,0.3)',
      color: '#10b981' } },
  content: {
    flexGrow: 1,
    minHeight: '100vh',
    padding: theme.spacing(4),
    background: theme.palette.background.default,
    backgroundImage: theme.palette.type === 'dark'
      ? 'radial-gradient(ellipse at 10% 0%, rgba(16,185,129,0.05) 0%, transparent 50%), radial-gradient(ellipse at 90% 100%, rgba(212,168,83,0.04) 0%, transparent 50%)'
      : 'radial-gradient(ellipse at 10% 0%, rgba(16,185,129,0.03) 0%, transparent 50%), radial-gradient(ellipse at 90% 100%, rgba(212,168,83,0.03) 0%, transparent 50%)',
    backgroundAttachment: 'fixed' } }));

export default function AdminLayout({ title, onRefresh, children }) {
  const classes = useStyles();
  
  const history = useHistory();
  const { user } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
    const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadFeedbacks, setUnreadFeedbacks] = useState(0);

  useEffect(() => {
    const unsubMessages = db.collection('support_chats')
      .where('unreadByAdmin', '==', true)
      .onSnapshot((snap) => {
        setUnreadMessages(snap.size);
      }, () => {});

    const unsubFeedbacks = db.collection('feedbacks')
      .where('acknowledged', '==', false)
      .onSnapshot((snap) => {
        setUnreadFeedbacks(snap.size);
      }, () => {});

    return () => {
      unsubMessages();
      unsubFeedbacks();
    };
  }, []);

  return (
    <div className={classes.root}>
      <Sidebar />
      <AppBar position="fixed" className={classes.appBar} elevation={0}>
        <Toolbar>
          <div className={classes.toolbarInner}>
            <div className={classes.titleArea}>
              <div className={classes.titleDot} />
              <Typography className={classes.title}>{title}</Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {onRefresh && (
                <Tooltip title="Refresh data" arrow>
                  <IconButton className={classes.iconBtn} onClick={onRefresh} size="small" style={{ marginRight: 16 }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'} arrow>
                <IconButton
                  className={classes.iconBtn}
                  onClick={toggleTheme}
                  size="small"
                  style={{ marginRight: 8 }}
                >
                  {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Messages" arrow>
                <IconButton
                  className={classes.iconBtn}
                  onClick={() => history.push('/messages')}
                  size="small"
                  style={{ marginRight: 8 }}
                >
                  <Badge
                    badgeContent={unreadMessages}
                    color="error"
                    invisible={unreadMessages === 0}
                    overlap="circular"
                  >
                    <ChatIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Feedback" arrow>
                <IconButton
                  className={classes.iconBtn}
                  onClick={() => history.push('/feedback')}
                  size="small"
                  style={{ marginRight: 16 }}
                >
                  <Badge
                    badgeContent={unreadFeedbacks}
                    color="error"
                    invisible={unreadFeedbacks === 0}
                    overlap="circular"
                  >
                    <FeedbackIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
              {user && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ textAlign: 'right' }}>
                    <Typography color="textPrimary" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {user.name || 'Admin'}
                    </Typography>
                    <Typography color="textSecondary" style={{ fontSize: '0.75rem' }}>
                      {user.phone || user.email}
                    </Typography>
                  </div>
                  <Avatar style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #10b981, #059669)', fontSize: '1rem', fontWeight: 600 }}>
                    {(user.name || user.email || 'A')[0].toUpperCase()}
                  </Avatar>
                </div>
              )}
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {children}
      </main>
    </div>
  );
}
